import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ArrowUpRight, Layers } from "lucide-react";
import api, { inr } from "./api";
import { PageHeader, EmptyState, inputCls, selectCls } from "./components";
import { EASE } from "@/components/Motion";

const PLATFORMS = ["all", "Instagram", "YouTube", "Facebook", "TikTok", "Telegram", "X", "Other"];
const PLATFORM_LABELS = { all: "All", X: "X / Twitter" };

export default function ServicesPage() {
  const navigate = useNavigate();
  const [services, setServices] = useState(null);
  const [search, setSearch] = useState("");
  const [platform, setPlatform] = useState("all");
  const [category, setCategory] = useState("all");

  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data)).catch(() => setServices([]));
  }, []);

  const categories = useMemo(() => {
    if (!services) return [];
    const pool = platform === "all" ? services : services.filter((s) => s.platform === platform);
    return [...new Set(pool.map((s) => s.category))];
  }, [services, platform]);

  const filtered = useMemo(() => {
    if (!services) return [];
    const q = search.trim().toLowerCase();
    return services.filter(
      (s) =>
        (platform === "all" || s.platform === platform) &&
        (category === "all" || s.category === category) &&
        (!q || s.name.toLowerCase().includes(q) || s.sid.includes(q))
    );
  }, [services, search, platform, category]);

  return (
    <div data-testid="services-page">
      <PageHeader title="Service Catalogue" sub="Every instrument in the arsenal — search, filter, deploy." />

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="relative min-w-56 flex-1">
          <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-600" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or service ID"
            className={`${inputCls} pl-11`}
            data-testid="services-search-input"
          />
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className={`${selectCls} w-auto`}
          data-testid="services-category-filter"
        >
          <option value="all">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      <div className="mb-8 flex flex-wrap gap-2">
        {PLATFORMS.map((p) => (
          <button
            key={p}
            onClick={() => { setPlatform(p); setCategory("all"); }}
            data-testid={`services-platform-filter-${p.toLowerCase()}`}
            className={`rounded-full border px-4 py-2 text-xs font-medium transition-all duration-300 ${
              platform === p
                ? "border-gold bg-gold/10 text-goldlight"
                : "border-white/10 text-neutral-400 hover:border-gold/40 hover:text-neutral-100"
            }`}
          >
            {PLATFORM_LABELS[p] || p}
          </button>
        ))}
      </div>

      {services === null ? (
        <div className="flex justify-center py-24">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold/20 border-t-gold" />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Layers} title="No services match" sub="Try clearing the search or switching platforms." testid="services-empty" />
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="overflow-hidden rounded-2xl border border-white/10"
          data-testid="services-table"
        >
          <div className="hidden grid-cols-12 gap-4 border-b border-white/10 bg-white/[0.02] px-6 py-3 font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-500 md:grid">
            <span className="col-span-1">ID</span>
            <span className="col-span-5">Service</span>
            <span className="col-span-2">Rate / 1K</span>
            <span className="col-span-1">Min</span>
            <span className="col-span-1">Max</span>
            <span className="col-span-2 text-right">Order</span>
          </div>
          {filtered.map((s) => (
            <div
              key={s.sid}
              data-testid={`service-row-${s.sid}`}
              className="grid grid-cols-2 items-center gap-3 border-b border-white/5 bg-panel/80 px-4 py-4 transition-colors duration-300 last:border-0 hover:bg-gold/[0.04] md:grid-cols-12 md:gap-4 md:px-6"
            >
              <span className="col-span-1 font-mono text-xs text-gold/80">#{s.sid}</span>
              <div className="col-span-2 md:col-span-5">
                <div className="text-sm font-medium text-neutral-100">{s.name}</div>
                <div className="mt-0.5 line-clamp-1 text-xs text-neutral-500">{s.description}</div>
                <div className="mt-1 font-mono text-[9px] tracking-[0.2em] text-neutral-600 md:hidden">
                  {inr(s.rate)}/1K · MIN {s.min.toLocaleString("en-IN")} · MAX {s.max.toLocaleString("en-IN")}
                </div>
              </div>
              <span className="hidden text-sm font-semibold text-goldlight md:col-span-2 md:block">{inr(s.rate)}</span>
              <span className="hidden text-sm text-neutral-400 md:col-span-1 md:block">{s.min.toLocaleString("en-IN")}</span>
              <span className="hidden text-sm text-neutral-400 md:col-span-1 md:block">{s.max.toLocaleString("en-IN")}</span>
              <div className="col-span-1 flex justify-end md:col-span-2">
                <button
                  onClick={() => navigate(`/panel/new-order?service=${s.sid}`)}
                  data-testid={`service-order-btn-${s.sid}`}
                  className="flex items-center gap-1.5 rounded-full border border-gold/30 px-4 py-2 text-xs font-semibold text-goldlight transition-all duration-300 hover:bg-gold hover:text-black"
                >
                  Order <ArrowUpRight size={12} />
                </button>
              </div>
            </div>
          ))}
        </motion.div>
      )}
    </div>
  );
}
